import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import Routes from 'lib/utils/routes';
import { throwSuccessToast, throwCriticalErrorToast } from 'lib/utils/toast';
import { createActivitySetInput } from 'lib/utils/activities';
import moment from 'moment';
import { TEMP_REFS } from 'lib/constants';
import { bugsnag } from 'lib/external/bugsnag';

import {
  START_ACTIVITY_WITH_LABOR_TICKET,
  START_ACTIVITY_WITH_LABOR_TICKET_NATS,
} from 'lib/api/mutations';
import {
  actionCanonicalActivitySetOpen,
  actionCanonicalActivitySetDelete,
  actionSetWorkOrderOpActionLoading,
  actionSetPendingActivityTypeRef,
  actionSetLaborTicketActionLoading,
  actionCloseLaborTicketModal,
} from 'lib/actions';
import { handleNatsLaborTicketResponse } from 'lib/utils/handleNatsLaborTicketResponse';
import { getActivityTypes } from 'lib/selectors/getActivityTypes';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { useIsLaborTicketFlow } from 'lib/hooks/useIsLaborTicketFlow';
import { useTranslation } from 'react-i18next';
import { getBugsnagUserInfo } from 'lib/selectors/getBugsnagUserInfo';
import { getPostLaborUpdatesOnNats } from 'lib/selectors/getPostLaborUpdatesOnNats';

const {
  ACTIVITY_REF: optimisticActivityRef,
  ACTIVITY_SET_REF: optimisticActivitySetRef,
} = TEMP_REFS;

const useStartActivityWithLaborTicket = ({ machine, workOrderOp }) => {
  const mainDispatch = useDispatch();
  const history = useHistory();
  const laborTicketEnabled = useIsLaborTicketFlow();
  const { t } = useTranslation();
  const bugsnagUserInfo = useSelector(getBugsnagUserInfo);

  const workOrderId = workOrderOp?.workOrderId;

  const operator = useSelector(getCurrentOperator);
  const activityTypes = useSelector(getActivityTypes);
  const natsFlowEnabled = useSelector(getPostLaborUpdatesOnNats);

  const mutationDoc = natsFlowEnabled
    ? START_ACTIVITY_WITH_LABOR_TICKET_NATS
    : START_ACTIVITY_WITH_LABOR_TICKET;

  const [requestStartActivityWithLaborTicket, { loading }] = useMutation(
    mutationDoc,
    {
      fetchPolicy: 'no-cache',
      onCompleted: async (res) => {
        if (natsFlowEnabled) {
          const { startActivityWithLaborTicketResponse } = res || {};
          // async endpoint returns { success, messageId, status, message, actionResults }
          if (startActivityWithLaborTicketResponse?.success) {
            handleNatsLaborTicketResponse({
              response: startActivityWithLaborTicketResponse,
              dispatch: mainDispatch,
              machine,
              options: { handleWorkOrderOp: true },
            });

            // Check for ERP errors even when operation succeeds
            const { erpResponses } = startActivityWithLaborTicketResponse;
            let hasErpErrors = false;
            if (erpResponses && Array.isArray(erpResponses)) {
              const erpErrors = erpResponses
                .filter((resp) => {
                  return resp.error || resp.response?.status === 'error';
                })
                .map((resp) => {
                  return resp.error || resp.response?.error?.message;
                })
                .filter(Boolean);

              if (erpErrors.length > 0) {
                hasErpErrors = true;
                erpErrors.forEach((error) => {
                  throwCriticalErrorToast(`ERP Error: ${error}`);
                });
              }
            }

            mainDispatch(actionSetLaborTicketActionLoading(false));
            mainDispatch(actionCloseLaborTicketModal());
            mainDispatch(actionSetWorkOrderOpActionLoading(false));
            mainDispatch(actionSetPendingActivityTypeRef(null));

            if (!hasErpErrors) {
              throwSuccessToast('Job started successfully!');
            }

            history.push(Routes.machineIdPath(machine.id));
            return;
          }
          // if async responded but not success, clear loading and show error
          mainDispatch(actionSetLaborTicketActionLoading(false));
          mainDispatch(actionSetWorkOrderOpActionLoading(false));
          mainDispatch(actionSetPendingActivityTypeRef(null));
          const errorMessage =
            startActivityWithLaborTicketResponse?.error ||
            startActivityWithLaborTicketResponse?.message ||
            'Async submission failed to start labor ticket';
          throwCriticalErrorToast(errorMessage);
          return;
        }

        const { startActivityWithLaborTicketResponse } = res || {};
        const {
          createActivitySet: {
            activitySet: {
              activities: [activity], // should be sorted start ASC
              ...activitySet
            },
          },
        } = startActivityWithLaborTicketResponse;
        await mainDispatch(
          actionCanonicalActivitySetOpen({
            activitySet,
            activity,
            optimisticActivitySetRef,
            optimisticActivityRef,
          })
        );

        mainDispatch(actionSetLaborTicketActionLoading(false));
        mainDispatch(actionCloseLaborTicketModal());

        throwSuccessToast(`Started order ${activitySet.workOrderId}`);
        mainDispatch(actionSetWorkOrderOpActionLoading(false));
        mainDispatch(actionSetPendingActivityTypeRef(null));
        history.push(Routes.machineIdPath(machine.id));
      },
      onError: (err) => {
        bugsnag.notify(err, (event) => {
          event.addMetadata('user', bugsnagUserInfo);
        });
        mainDispatch(actionSetLaborTicketActionLoading(false));
        throwCriticalErrorToast(`${t('Failed to open labor ticket')}: ${err}`);

        mainDispatch(
          actionCanonicalActivitySetDelete({
            activitySetRef: optimisticActivitySetRef,
          })
        );
        mainDispatch(actionSetWorkOrderOpActionLoading(false));
        mainDispatch(actionSetPendingActivityTypeRef(null));
      },
    }
  );

  return async ({ activityTypeRef, partOperation, startAt }) => {
    if (!loading && laborTicketEnabled && workOrderOp && partOperation) {
      const nowISO = moment().toISOString();

      const activityMode = activityTypes.find((type) => {
        return type.activityTypeRef === activityTypeRef;
      })?.activityMode;

      mainDispatch(actionSetWorkOrderOpActionLoading(true));
      mainDispatch(actionSetLaborTicketActionLoading(true));

      const activitySetInput = createActivitySetInput({
        machine,
        workOrderOp,
        partOperation,
        activityTypeRef,
        startAt,
      });

      await requestStartActivityWithLaborTicket({
        variables: {
          input: {
            createLaborTicket: {
              clockIn: startAt || nowISO,
              clockOut: null,
              lot: workOrderOp.lot,
              sequenceNumber: workOrderOp.sequenceNumber,
              split: workOrderOp.split,
              sub: workOrderOp.sub,
              state: 'OPEN',
              resourceId: machine.erpResourceId,
              personId: operator.erpId,
              workOrderId,
              type: activityMode,
              goodParts: 0,
              badParts: 0,
            },
            createActivitySet: activitySetInput,
          },
        },
      });
    }
  };
};

export default useStartActivityWithLaborTicket;
